import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const UserInfoForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    accountNumber: ''
  });
  const [errors, setErrors] = useState({});
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const navigate = useNavigate();

  const checkForDuplicate = async (name, accountNumber) => {
    // Need both name and account number to check for duplicates
    if (!name || name.trim().length < 2 || !accountNumber || accountNumber.length < 3) {
      setDuplicateInfo(null);
      setShowDuplicateWarning(false);
      return;
    }

    setCheckingDuplicate(true);
    try {
      const response = await axios.get('/api/check-duplicate', {
        params: {
          name: name.trim(),
          account_number: accountNumber.trim()
        }
      });
      const data = response.data;
      
      if (data.has_existing_submission) {
        setDuplicateInfo(data);
        setShowDuplicateWarning(true);
      } else {
        setDuplicateInfo(null);
        setShowDuplicateWarning(false);
      }
    } catch (error) {
      console.error('Error checking for duplicate:', error);
      // Don't show error to user for this check, just log it
      setDuplicateInfo(null);
      setShowDuplicateWarning(false);
    } finally {
      setCheckingDuplicate(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Effect to handle debounced duplicate checking
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      checkForDuplicate(formData.name.trim(), formData.accountNumber.trim());
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [formData.name, formData.accountNumber]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    
    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = 'Account number is required';
    } else if (!/^[a-zA-Z0-9]+$/.test(formData.accountNumber.trim())) {
      newErrors.accountNumber = 'Account number can only contain letters and numbers';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Block submission if duplicate is detected
      if (showDuplicateWarning) {
        return; // Form submission is blocked
      }

      // Store form data in sessionStorage for next step
      sessionStorage.setItem('userInfo', JSON.stringify({
        name: formData.name.trim(),
        accountNumber: formData.accountNumber.trim()
      }));
      
      navigate('/upload');
    }
  };

  return (
    <div className="form-container">
      <h2>Licensee Information</h2>
      <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '2rem', fontSize: '1.1rem' }}>
        Please provide your information to begin the documentation process
      </p>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Licensee Owner Name (not entity name) *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter your Licensee Owner Name (not entity name)"
            className={errors.name ? 'error' : ''}
          />
          {errors.name && <span className="error-message">{errors.name}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="accountNumber">Account Number *</label>
          <input
            type="text"
            id="accountNumber"
            name="accountNumber"
            value={formData.accountNumber}
            onChange={handleChange}
            placeholder="Enter your account number"
            className={errors.accountNumber ? 'error' : ''}
          />
          {checkingDuplicate && (
            <div className="status-message status-info">
              Checking for existing submissions...
            </div>
          )}
          {errors.accountNumber && <span className="error-message">{errors.accountNumber}</span>}
          
          {showDuplicateWarning && duplicateInfo && (
            <div className="status-message status-error">
              <strong>❌ Duplicate Submission Detected</strong>
              <p>
                <strong>{duplicateInfo.name}</strong> with Account <strong>{duplicateInfo.account_number}</strong> already submitted documents.
                Each licensee can only submit once for this event.
              </p>
            </div>
          )}
        </div>

        <div className="button-group">
          {showDuplicateWarning ? (
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={() => {
                setFormData({name: '', accountNumber: ''});
                setShowDuplicateWarning(false);
                setDuplicateInfo(null);
              }}
            >
              ← Clear Form & Try Again
            </button>
          ) : (
            <button type="submit" className="btn btn-primary">
              Continue to Image Upload →
            </button>
          )}
        </div>
        
        <div className="disclaimer-section">
          <p>
            📋 Important Notice
          </p>
          <p>
            Ensure handwritten text is <strong>clear and legible</strong> for AI analysis.
          </p>
          <br />
          <p>
            <strong>One submission per licensee only.</strong> Contact support for changes.
          </p>
        </div>
        
        <p style={{ 
          textAlign: 'center', 
          color: '#9ca3af',
          marginTop: '0.75rem', 
          fontSize: '0.8rem',
          fontStyle: 'italic'
        }}>
          
        </p>
      </form>
    </div>
  );
};

export default UserInfoForm;
